/**
 * The bridge between the two filter shapes.
 *
 * F 280 stopped on a collision and named it: `ViewConfig.filter` is
 * `FilterRule[]` — a flat AND, because `applyViewRules` says *"every rule must
 * pass (AND) — the common Notion semantics"* — while FCC's `PropertyFilter` is
 * `Record<string, string[]>` — a grouped OR, "status is one of these".
 *
 * Flattening `{status: ['a','b']}` into `[{status is a}, {status is b}]` asks
 * for `status === 'a' AND status === 'b'`, which matches nothing, so a filter
 * that means "open OR done" would hide every row and look like a working filter
 * to nobody.
 *
 * So neither shape moves. This file is the ONLY place the two meet, and it
 * converts in both directions:
 *
 *   PropertyFilter -> FilterRule[]   one `is-any-of` rule per property
 *   FilterRule[]   -> PropertyFilter `is-any-of`/`is` collapse to the group
 *
 * The round trip is lossless for everything `PropertyFilter` can express, which
 * is the property worth stating: `applyFiltersToPropertyFilter` is the inverse
 * of `propertyFilterToFilterRules` for every input the first can produce. A
 * test asserts that, because a converter that quietly loses filters is worse
 * than the AND it replaced.
 *
 * WHAT THIS DOES NOT DO: it does not change `ViewConfig`'s shape, and it does
 * not make `PropertyFilter` an AND. Two shapes, two sides, one seam.
 */

import type { FilterRule, FilterOperator } from './viewConfig';

/**
 * FCC's filter. Structurally identical to the real
 * `src/ui/components/database/calendarConfig.ts` `PropertyFilter`
 * (`Record<string, string[]>`) and declared here so the core does not import
 * from the UI layer — which would invert the dependency the F 135 split
 * created. A test asserts the two declarations still match.
 */
export type PropertyFilterLike = Record<string, string[]>;

/** Operators whose value is a list of acceptable values, not a single one. */
const SET_OPERATORS: ReadonlySet<FilterOperator> = new Set<FilterOperator>([
  'is-any-of',
  'is',
  'is-not',
]);

/**
 * `PropertyFilter` -> `FilterRule[]`.
 *
 * ONE rule per property, carrying the whole group as its value. That is the
 * whole point: one rule means one clause in the AND, and the OR lives inside
 * it.
 *
 * A property with an EMPTY candidate list is DROPPED, not emitted as an
 * `is-any-of: []`. `matches()` returns false for an empty candidate list
 * (deliberately — "matches any of none" is not a filter), so emitting one would
 * make an empty checkbox group hide every row. Dropping it is what "no
 * selection" has always meant in the FCC UI.
 */
export function propertyFilterToFilterRules(filter: PropertyFilterLike | undefined | null): FilterRule[] {
  if (!filter) return [];
  const rules: FilterRule[] = [];
  for (const [propertyId, values] of Object.entries(filter)) {
    if (!Array.isArray(values)) continue;
    const wanted = values.filter((v) => v !== undefined && v !== null);
    if (wanted.length === 0) continue;   // "no selection", not "match nothing"
    rules.push({ propertyId, operator: 'is-any-of', value: wanted });
  }
  return rules;
}

/**
 * `FilterRule[]` -> `PropertyFilter`.
 *
 * The inverse, for a view that was authored against `FilterRule[]` and has to
 * be shown in an FCC surface that speaks `PropertyFilter`.
 *
 * `is-any-of` and `is` are the value-carrying operators a group can hold. Rules
 * with any other operator are NOT representable in a `PropertyFilter` — a
 * `contains` cannot survive a trip through `string[]` without being turned into
 * a lie — so they are DROPPED, and `unconvertibleFilterRules` reports which.
 *
 * Dropping silently would be the dangerous version, so the caller can ask what
 * did not survive instead of discovering it as a filter that does not apply.
 */
export function filterRulesToPropertyFilter(rules: FilterRule[] | undefined | null): PropertyFilterLike {
  const out: PropertyFilterLike = {};
  for (const rule of rules ?? []) {
    if (!SET_OPERATORS.has(rule.operator)) continue;
    const list = Array.isArray(rule.value) ? rule.value : [rule.value];
    const existing = out[rule.propertyId] ?? [];
    for (const v of list) {
      if (v === undefined || v === null) continue;
      if (!existing.includes(v as string)) existing.push(v as string);
    }
    if (existing.length > 0) out[rule.propertyId] = existing;
  }
  return out;
}

/** Which rules cannot be expressed as a `PropertyFilter`, and why. */
export function unconvertibleFilterRules(rules: FilterRule[] | undefined | null): FilterRule[] {
  return (rules ?? []).filter((r) => !SET_OPERATORS.has(r.operator));
}

/**
 * Is every rule in this view representable as a `PropertyFilter`?
 *
 * The question FCC's loader should ask BEFORE it swaps a view's filter
 * representation, because the answer decides whether the conversion is
 * lossless or lossy — and a lossy swap must be a decision, not a default.
 */
export function isPropertyFilterLossless(rules: FilterRule[] | undefined | null): boolean {
  return unconvertibleFilterRules(rules).length === 0;
}
